/**
 * Tax Calculation Validation Utilities
 * Ensures consistency and correctness of tax calculations across the application
 */

import type { SteuerParameter } from '@/types';
import { TAX_RATES, SOLI_THRESHOLDS_2025, SOLI_THRESHOLDS_2026, KIRCHENSTEUER_OPTIONEN, calculateEffectiveCapitalGainsTax } from './constants';

/**
 * Tax calculation result interface for validation
 */
export interface TaxCalculationResult {
  kapitalertragsteuer: number;
  solidaritaetszuschlag: number;
  kirchensteuer: number;
  gesamtsteuer: number;
  steuerpflichtigerGewinn: number;
}

/**
 * Income tax calculation result interface
 */
export interface IncomeTaxResult {
  einkommensteuer: number;
  solidaritaetszuschlag: number;
  kirchensteuer: number;
  gesamt: number;
  taxableIncome: number;
}

/**
 * Tax validation errors
 */
export class TaxValidationError extends Error {
  constructor(message: string, public details?: unknown) {
    super(message);
    this.name = 'TaxValidationError';
  }
}

/**
 * Validates capital gains tax calculation
 */
export function validateCapitalGainsTax(
  steuerpflichtigerGewinn: number,
  steuerParameter: SteuerParameter | null,
  result: TaxCalculationResult
): void {
  const tolerance = 0.01; // 1 cent tolerance for rounding

  // Verlustposition: keine Kapitalertragsteuer; calculateEffectiveCapitalGainsTax liefert 0
  if (steuerpflichtigerGewinn <= 0) {
    const expectedZero = calculateEffectiveCapitalGainsTax(0, steuerParameter?.kirchensteuer);
    if (Math.abs(result.kapitalertragsteuer - expectedZero.kapitalertragsteuer) > tolerance) {
      throw new TaxValidationError('Kapitalertragsteuer must be 0 for non-positive gain', {
        steuerpflichtigerGewinn,
        actual: result.kapitalertragsteuer
      });
    }
    if (Math.abs(result.solidaritaetszuschlag - expectedZero.solidaritaetszuschlag) > tolerance) {
      throw new TaxValidationError('Solidaritätszuschlag must be 0 for non-positive gain', {
        steuerpflichtigerGewinn
      });
    }
    if (Math.abs(result.kirchensteuer - expectedZero.kirchensteuer) > tolerance) {
      throw new TaxValidationError('Kirchensteuer must be 0 for non-positive gain', {
        steuerpflichtigerGewinn
      });
    }
    if (Math.abs(result.gesamtsteuer - expectedZero.gesamtsteuer) > tolerance) {
      throw new TaxValidationError('Gesamtsteuer must be 0 for non-positive gain', {
        steuerpflichtigerGewinn
      });
    }
    return;
  }

  // Validate tax calculation using the same method as the actual calculation
  const expectedTaxCalculation = calculateEffectiveCapitalGainsTax(steuerpflichtigerGewinn, steuerParameter?.kirchensteuer);

  // Validate Kapitalertragsteuer calculation
  if (Math.abs(result.kapitalertragsteuer - expectedTaxCalculation.kapitalertragsteuer) > tolerance) {
    throw new TaxValidationError(
      'Kapitalertragsteuer calculation mismatch',
      {
        expected: expectedTaxCalculation.kapitalertragsteuer,
        actual: result.kapitalertragsteuer,
        difference: Math.abs(result.kapitalertragsteuer - expectedTaxCalculation.kapitalertragsteuer)
      }
    );
  }

  // Validate Solidaritätszuschlag calculation
  if (Math.abs(result.solidaritaetszuschlag - expectedTaxCalculation.solidaritaetszuschlag) > tolerance) {
    throw new TaxValidationError(
      'Solidaritätszuschlag calculation mismatch',
      {
        expected: expectedTaxCalculation.solidaritaetszuschlag,
        actual: result.solidaritaetszuschlag,
        difference: Math.abs(result.solidaritaetszuschlag - expectedTaxCalculation.solidaritaetszuschlag)
      }
    );
  }

  // Validate Kirchensteuer calculation
  if (Math.abs(result.kirchensteuer - expectedTaxCalculation.kirchensteuer) > tolerance) {
    throw new TaxValidationError(
      'Kirchensteuer calculation mismatch',
      {
        expected: expectedTaxCalculation.kirchensteuer,
        actual: result.kirchensteuer,
        kirchensteuerType: steuerParameter?.kirchensteuer
      }
    );
  }

  // Validate total tax calculation
  if (Math.abs(result.gesamtsteuer - expectedTaxCalculation.gesamtsteuer) > tolerance) {
    throw new TaxValidationError(
      'Total tax calculation mismatch',
      {
        expected: expectedTaxCalculation.gesamtsteuer,
        actual: result.gesamtsteuer,
        components: {
          kapitalertragsteuer: result.kapitalertragsteuer,
          solidaritaetszuschlag: result.solidaritaetszuschlag,
          kirchensteuer: result.kirchensteuer
        }
      }
    );
  }
}

/**
 * Validates income tax calculation for Modellvergleich
 */
export function validateIncomeTax(
  taxableIncome: number,
  familienstand: string,
  kirchensteuerSatz: string | undefined,
  steuerjahr: number,
  result: IncomeTaxResult
): void {
  // Validate input parameters
  if (taxableIncome < 0) {
    throw new TaxValidationError('Taxable income cannot be negative', { taxableIncome });
  }

  if (!['alleinstehend', 'verheiratet'].includes(familienstand)) {
    throw new TaxValidationError('Invalid familienstand', { familienstand });
  }

  // Validate Solidaritätszuschlag thresholds (year-based)
  const soliThresholds = steuerjahr >= 2026 ? SOLI_THRESHOLDS_2026 : SOLI_THRESHOLDS_2025;
  const soliFreigrenze = familienstand === 'verheiratet'
    ? soliThresholds.FREIGRENZE_MARRIED
    : soliThresholds.FREIGRENZE_SINGLE;

  // §3 Abs. 3 SolZG: Freigrenze bezieht sich auf die festgesetzte ESt, nicht auf zvE
  if (result.einkommensteuer <= soliFreigrenze && result.solidaritaetszuschlag > 0) {
    throw new TaxValidationError(
      'Solidaritätszuschlag should be 0 when ESt is below Freigrenze',
      {
        einkommensteuer: result.einkommensteuer,
        soliFreigrenze,
        solidaritaetszuschlag: result.solidaritaetszuschlag
      }
    );
  }

  // Validate Kirchensteuer rate
  if (kirchensteuerSatz && kirchensteuerSatz !== 'keine') {
    const kirchensteuerConfig = KIRCHENSTEUER_OPTIONEN.find(k => k.key === kirchensteuerSatz);
    if (!kirchensteuerConfig) {
      throw new TaxValidationError('Invalid Kirchensteuer configuration', { kirchensteuerSatz });
    }
  }

  // Validate that all components are non-negative
  if (result.einkommensteuer < 0 || result.solidaritaetszuschlag < 0 || result.kirchensteuer < 0) {
    throw new TaxValidationError(
      'Tax components cannot be negative',
      {
        einkommensteuer: result.einkommensteuer,
        solidaritaetszuschlag: result.solidaritaetszuschlag,
        kirchensteuer: result.kirchensteuer
      }
    );
  }

  // Validate total calculation with adaptive tolerance
  const expectedGesamt = result.einkommensteuer + result.solidaritaetszuschlag + result.kirchensteuer;
  
  // Use adaptive tolerance: minimum 2 cents or 0.001% of the amount, whichever is larger
  const percentageTolerance = Math.max(expectedGesamt * 0.00001, 0.02);
  const difference = Math.abs(result.gesamt - expectedGesamt);
  
  if (difference > percentageTolerance) {
    throw new TaxValidationError(
      'Total income tax calculation mismatch',
      {
        expected: expectedGesamt,
        actual: result.gesamt,
        difference: difference,
        tolerance: percentageTolerance,
        components: {
          einkommensteuer: result.einkommensteuer,
          solidaritaetszuschlag: result.solidaritaetszuschlag,
          kirchensteuer: result.kirchensteuer
        }
      }
    );
  }
}

/**
 * Validates Sparer-Pauschbetrag application
 */
export function validateSparerPauschbetrag(
  familienstand: string,
  verwendeterPauschbetrag: number,
  sparerPauschbetragEnabled: boolean
): void {
  if (!sparerPauschbetragEnabled && verwendeterPauschbetrag > 0) {
    throw new TaxValidationError(
      'Sparer-Pauschbetrag used but not enabled',
      { verwendeterPauschbetrag, sparerPauschbetragEnabled }
    );
  }

  if (sparerPauschbetragEnabled) {
    const maxPauschbetrag = familienstand === 'verheiratet' ? 2000 : 1000;
    
    if (verwendeterPauschbetrag > maxPauschbetrag) {
      throw new TaxValidationError(
        'Sparer-Pauschbetrag exceeds maximum',
        {
          verwendeterPauschbetrag,
          maxPauschbetrag,
          familienstand
        }
      );
    }
  }
}

/**
 * Validates effective tax rate calculation
 */
export function validateEffectiveTaxRate(
  steuerParameter: SteuerParameter | null,
  calculatedRate: number
): void {
  let expectedRate: number = TAX_RATES.BASE_CAPITAL_GAINS_RATE; // Base capital gains rate from TAX_RATES
  
  if (steuerParameter?.kirchensteuer === 'baden-wuerttemberg-bayern') {
    expectedRate = TAX_RATES.COMBINED_RATE_BW_BAYERN; // Correct combined rate with church tax
  } else if (steuerParameter?.kirchensteuer === 'andere-bundeslaender') {
    expectedRate = TAX_RATES.COMBINED_RATE_ANDERE; // Correct combined rate with church tax
  }

  const tolerance = 0.001; // 0.1% tolerance
  
  if (Math.abs(calculatedRate - expectedRate) > tolerance) {
    throw new TaxValidationError(
      'Effective tax rate calculation mismatch',
      {
        expected: expectedRate,
        actual: calculatedRate,
        kirchensteuer: steuerParameter?.kirchensteuer
      }
    );
  }
}

/**
 * Development mode validation wrapper
 * Only performs validation in development environment
 * 
 * Note: Small validation warnings (0.01-0.02€) are normal due to JavaScript's 
 * floating-point arithmetic precision. These do not indicate calculation errors.
 */
export function devValidate<T extends any[]>(
  validationFn: (...args: T) => void,
  ...args: T
): void {
  if (import.meta.env.DEV) {
    try {
      validationFn(...args);
    } catch {
      // Validation errors are silently caught in production
      // In development, they can be inspected via debugger or error tracking
    }
  }
}
